import { Head, Link, router, usePage } from '@inertiajs/react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import OrgPostFeedItem from '@/components/feed/OrgPostFeedItem'
import UserRsvpFeedItem from '@/components/feed/UserRsvpFeedItem'
import UserReactionFeedItem from '@/components/feed/UserReactionFeedItem'
import type { FeedItem, FeaturedOrgCard, SharedProps } from '@/types'

type Props = { items: FeedItem[]; featured_orgs: FeaturedOrgCard[] }

export default function FeedIndex({ items, featured_orgs }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const canReact = !!current_user

  return (
    <>
      <Head title="Feed" />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="mb-6 text-3xl font-semibold tracking-tight">Your feed</h1>

        {items.length === 0 ? (
          <EmptyFeed orgs={featured_orgs} />
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              if (item.verb === 'org_posted') {
                return <OrgPostFeedItem key={item.id} post={item.post} canReact={canReact} />
              }
              if (item.verb === 'user_rsvped') {
                return <UserRsvpFeedItem key={item.id} item={item} />
              }
              return <UserReactionFeedItem key={item.id} item={item} />
            })}
          </div>
        )}
      </div>
    </>
  )
}

function EmptyFeed({ orgs }: { orgs: FeaturedOrgCard[] }) {
  const hasOrgs = orgs.length > 0
  return (
    <Card>
      <CardContent className="space-y-6 p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Your feed is empty</h2>
          <p className="mt-2 text-base text-muted-foreground">
            {hasOrgs
              ? 'Follow organizations to see their updates here. Here are a few to get you started.'
              : 'Follow organizations to see their updates here.'}
          </p>
        </div>

        {hasOrgs && (
          <div className="grid gap-3 sm:grid-cols-2">
            {orgs.map((org) => (
              <FeaturedOrgRow key={org.id} org={org} />
            ))}
          </div>
        )}

        <div className="text-center">
          <Button asChild variant="outline">
            <Link href="/events">Browse all events</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function FeaturedOrgRow({ org }: { org: FeaturedOrgCard }) {
  function toggleFollow() {
    if (org.is_following) {
      router.delete(`/o/${org.slug}/follow`, { preserveScroll: true })
    } else {
      router.post(`/o/${org.slug}/follow`, {}, { preserveScroll: true })
    }
  }
  return (
    <div className="flex items-center gap-3 rounded-xl border border-black/5 bg-muted/30 p-3">
      <Link href={`/o/${org.slug}`} className="shrink-0">
        <Avatar className="h-10 w-10">
          {org.logo_url && <AvatarImage src={org.logo_url} alt={org.name} />}
          <AvatarFallback>{org.name[0]}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          href={`/o/${org.slug}`}
          className="flex items-center gap-1 font-medium text-foreground hover:underline"
        >
          <span className="truncate">{org.name}</span>
          {org.verified && <VerifiedBadge />}
        </Link>
        {org.primary_cause && (
          <p className="truncate text-xs text-muted-foreground">{org.primary_cause}</p>
        )}
      </div>
      <Button size="sm" variant={org.is_following ? 'outline' : 'default'} onClick={toggleFollow}>
        {org.is_following ? 'Following' : '+ Follow'}
      </Button>
    </div>
  )
}
